/**
 * Workflow Canvas Component
 * Main React Flow canvas for workflow editing
 */

import React, { useCallback, useRef } from 'react';
import { LockIcon, BarChartIcon } from '@bizfirst/common-react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  NodeChange,
  EdgeChange,
  ReactFlowInstance,
  BackgroundVariant,
  ConnectionLineType,
  PanOnScrollMode,
  useViewport,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useWorkflowStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { useDesignerModeStore } from '@flow-studio/store';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useNodeDrag } from '../../hooks/useNodeDrag';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useNodeExecutionSync } from '../../hooks/execution-visualization';
import {
  validateNodeForPort,
  type PortType,
  type NodeCategory,
} from '../../rules/portNodeSelectionRules';

import { CustomNode } from '../Nodes/CustomNode';
import { CircleNode } from '../Nodes/CircleNode';
import { DiamondNode } from '../Nodes/DiamondNode';
import { AIAgentNode } from '../Nodes/AIAgentNode';
import { AIFunctionNode } from '../Nodes/AIFunctionNode';
import { SubFlowNode } from '../Nodes/SubFlowNode';
import { CustomEdge } from '../Edges/CustomEdge';
import { SmartEdge } from '../Edges/SmartEdge';
import { NodeContextMenu } from '../ContextMenus/NodeContextMenu';
import { CanvasContextMenu } from '../ContextMenus/CanvasContextMenu';
import { CanvasControls } from '../Controls/CanvasControls';
import { ConfirmDialog } from '../Support/ConfirmDialog';
import { PinNodeDialog } from '../Modals/PinNodeDialog';

import { toReactFlowNodes, toReactFlowEdges, fromReactFlowNodes, fromReactFlowEdges, generateId } from '@flow-studio/services';
import { getNodeDisplayProps, type ProcessElementPort } from '@flow-studio/types';

// Node shape types — shapes only, never specific node codes.
// node.type is always one of these shapes (set by categoryToShape in workflowMappers / NodeFactoryService).
// The API drives which nodes exist; code must not know about specific node codes.
const nodeTypes = {
  'circle':   CircleNode,   // trigger category
  'diamond':  DiamondNode,  // logic / conditions category
  'ai-agent': AIAgentNode,  // ai-agent category (multi-port)
  'ai-function-node': AIFunctionNode, // ai-function code (any functional category)
  'subflow':  SubFlowNode,  // workflow category (sub-workflow)
  'custom':   CustomNode,   // everything else
  'default':  CustomNode,   // React Flow fallback
};

// Edge types mapping
const edgeTypes = {
  default: CustomEdge,
  straight: CustomEdge,
  step: CustomEdge,
  smoothstep: CustomEdge,
  smart: SmartEdge, // Smart edge that avoids nodes
};

/** Elements for which Space keeps its native meaning (activate / type / toggle). */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="tab"], [contenteditable=""], [contenteditable="true"]';

/**
 * Space engages the hand tool only when focus is on the page body or inside the canvas, and never
 * when the target is an interactive element (so buttons, inputs, menus and dialogs keep Space).
 */
function isSpacePanTarget(target: EventTarget | null, canvas: HTMLElement | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest(INTERACTIVE_SELECTOR)) return false;
  return target === document.body || (!!canvas && canvas.contains(target));
}

export const WorkflowCanvas: React.FC = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(null);
  const [pinDialogNodeId, setPinDialogNodeId] = React.useState<string | null>(null);
  const isInternalNodeUpdate = useRef(false);
  const isInternalEdgeUpdate = useRef(false);
  const positionSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFitWorkflowRef = useRef<string | null>(null);

  const { workflow, nodes: storeNodes, edges: storeEdges, setNodes: setStoreNodes, setEdges: setStoreEdges, addEdge: addStoreEdge, deleteNode, deleteEdge, updateNode } = useWorkflowStore();
  const { showMinimap, snapToGrid, gridSize, theme, contextMenu, closeContextMenu, setSelectedNodes, setSelectedEdges, selectedHandle, setSelectedHandle, useSmartEdges, pendingDeleteNodeIds, pendingDeleteEdgeIds, clearPendingDelete, clearSelection, showToast, hideSatellites, hiddenNodeIds, isCanvasLocked, autoSaveEnabled, setViewport: saveViewport, viewport: savedViewport, hideComponentsGlobally } = useUIStore() as any;

  // Three-mode designer - determine if canvas is read-only
  const mode = useDesignerModeStore((s) => s.mode);
  const isReadOnly = mode !== 'design';

  // Debug logging
  React.useEffect(() => {
    console.log('Store nodes:', storeNodes);
    console.log('Store edges:', storeEdges);
  }, [storeNodes, storeEdges]);

  const { onDragOver, onDrop } = useNodeDrag();
  const { handleCanvasContextMenu, handleNodeContextMenu } = useContextMenu(reactFlowInstance);

  // Live viewport (x, y, zoom) — re-renders on every pan/zoom frame, used to keep open
  // context menus anchored to their flow-space click point instead of a stale screen position.
  const viewport = useViewport();

  // Hand (pan) tool: toggled by the control button, or held via Space.
  // While active, nodes can't be dragged/connected/selected/deleted so dragging always moves the whole canvas.
  const [handToggled, setHandToggled] = React.useState(false);
  const [spaceHeld, setSpaceHeld] = React.useState(false);
  const handMode = handToggled || spaceHeld;
  const toggleHand = useCallback(() => setHandToggled((v) => !v), []);
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (!isSpacePanTarget(e.target, reactFlowWrapper.current)) return;
      e.preventDefault();
      setSpaceHeld(true);
    };
    const up = (e: KeyboardEvent) => { if (e.code === 'Space') setSpaceHeld(false); };
    const blur = () => setSpaceHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  // Use keyboard shortcuts (Delete is suppressed while the hand tool is active)
  useKeyboardShortcuts({ deleteDisabled: handMode });

  // Convert store nodes/edges to React Flow format
  const [nodes, setNodes, onNodesChange] = useNodesState(toReactFlowNodes(storeNodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(toReactFlowEdges(storeEdges));

  // Always-current ref to React Flow nodes/edges — used in callbacks to avoid stale closures.
  // Must be declared AFTER useNodesState/useEdgesState so `nodes`/`edges` are in scope.
  const latestNodesRef = useRef(nodes);
  latestNodesRef.current = nodes;
  const latestEdgesRef = useRef(edges);
  latestEdgesRef.current = edges;

  // Apply hidden flag: global satellite toggle + component toggle + per-node hidden IDs
  const displayNodes = React.useMemo(() => {
    const perNodeHidden: string[] = hiddenNodeIds ?? [];
    // Identify component nodes (targets of 'component' handle edges) so they can be
    // hidden independently via the "Hide Components" global toggle
    const componentNodeIds = new Set(
      storeEdges.filter(e => e.targetHandle === 'component').map(e => e.target)
    );
    return nodes.map(n => {
      const globallyHidden = hideSatellites && n.data.isSatellite;
      const componentHidden = (hideComponentsGlobally ?? true) && componentNodeIds.has(n.id);
      const locallyHidden = perNodeHidden.includes(n.id);
      return (globallyHidden || componentHidden || locallyHidden) ? { ...n, hidden: true } : n;
    });
  }, [nodes, hideSatellites, hiddenNodeIds, hideComponentsGlobally, storeEdges]);

  // Sync execution states to nodes (displays visual badges during execution)
  useNodeExecutionSync(setNodes);

  // Sync React Flow nodes/edges with store (only when store changes externally).
  // Uses the setNodes updater form to preserve the current React Flow selection state —
  // if we pass a plain array without selected:true, React Flow emits deselect changes
  // that wipe the UIStore selection before onSelectionChange can re-set it.
  React.useEffect(() => {
    if (!isInternalNodeUpdate.current) {
      console.log('🔄 [WorkflowCanvas] Syncing nodes from store to React Flow');
      setNodes((currentRfNodes) => {
        const rfNodes = toReactFlowNodes(storeNodes);
        const selectedIds = new Set(currentRfNodes.filter(n => n.selected).map(n => n.id));
        return rfNodes.map(n => ({ ...n, selected: selectedIds.has(n.id) }));
      });
    } else {
      isInternalNodeUpdate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNodes]);

  // Fit the view whenever a new workflow loads (nodes + instance both ready)
  React.useEffect(() => {
    const wfId = workflow?.workflowId != null ? String(workflow.workflowId) : null;
    if (
      wfId &&
      wfId !== lastFitWorkflowRef.current &&
      storeNodes.length > 0 &&
      reactFlowInstance
    ) {
      lastFitWorkflowRef.current = wfId;
      setTimeout(() => {
        reactFlowInstance.fitView({ padding: 0.15, maxZoom: 1, duration: 400 });
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow, storeNodes, reactFlowInstance]);

  // Edge store → ReactFlow sync. Always fires unless the change originated from
  // handleEdgesChange (isInternalEdgeUpdate), which means ReactFlow already has the
  // latest state and we only need to reset the flag.
  React.useEffect(() => {
    if (!isInternalEdgeUpdate.current) {
      setEdges(toReactFlowEdges(storeEdges));
    } else {
      isInternalEdgeUpdate.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeEdges]);

  // Recompute isSatellite on every edge change (add or remove) and whenever
  // the React Flow nodes list changes (e.g. a new satellite node was just added).
  // Two-pass logic:
  //  • "service" edges  (LLM→Agent, Tool→Agent, etc.): SOURCE node is satellite
  //    when it feeds into a port with isSatellite=true on the target.
  //  • "management" edges (team-leader, extends): TARGET node is satellite;
  //    the source (the leader/parent) must NOT be marked satellite.
  // NOTE: uses storeEdges directly (not latestEdgesRef) because the ref lags
  // one render behind — on deletion the ref still contains the removed edge.
  // NOTE: `nodes` is included as a dependency so that when NodeSearchModal adds a
  // new satellite node and its connecting edge in the same store batch, the effect
  // re-runs after the node sync effect has flushed the new node into React Flow
  // state — otherwise the new node is absent from rfNodes and never gets marked.
  React.useEffect(() => {
    const rfNodes = nodes;

    const MANAGEMENT_HANDLES = ['team-leader', 'extends'];
    const satelliteMap = new Map<string, ProcessElementPort | undefined>();

    for (const e of storeEdges) {
      if (MANAGEMENT_HANDLES.includes(e.sourceHandle ?? '')) {
        // Management port: the node being managed (target) is satellite
        satelliteMap.set(e.target, undefined);
      } else if (e.targetHandle === 'component') {
        // Component port: the node acting as a component (target) is satellite
        satelliteMap.set(e.target, undefined);
      } else {
        // Service port: source is satellite if it feeds a satellite input
        const tNode = rfNodes.find(n => n.id === e.target);
        const satellitePort = (tNode?.data?.processElement?.ports ?? [] as ProcessElementPort[])
          .find((p: ProcessElementPort) => p.portType === 'input' && p.portKey === e.targetHandle && p.isSatellite);
        if (satellitePort) satelliteMap.set(e.source, satellitePort);
      }
    }

    const updates: Array<{ id: string; isSatellite: boolean; satellite?: { satellite?: string } }> = [];

    for (const node of rfNodes) {
      const nowSatellite = satelliteMap.has(node.id);
      // Use !! to normalize undefined/null to false so freshly-loaded nodes
      // (where isSatellite was never explicitly set) don't trigger spurious
      // updateNode() calls that would set isDirty: true after every save.
      if (!!node.data.isSatellite !== nowSatellite) {
        updates.push({ id: node.id, isSatellite: nowSatellite, satellite: satelliteMap.get(node.id)?.satellite });
      }
    }

    if (updates.length > 0) {
      updates.forEach(u => {
        const node = rfNodes.find(n => n.id === u.id);
        if (node) updateNode(u.id, { data: { ...node.data, isSatellite: u.isSatellite, satellite: u.satellite } });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeEdges, nodes]);

  // Handle node changes (position, dimensions, etc.) — selection is handled by onSelectionChange.
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // Apply changes to React Flow nodes first
      onNodesChange(changes);

      // Skip store update if this is from an internal sync
      if (isInternalNodeUpdate.current) {
        isInternalNodeUpdate.current = false;
        return;
      }

      // Sync node positions/state to the workflow store.
      // latestNodesRef is updated every render so it's always current after
      // React processes the onNodesChange batch (which runs before the macrotask).
      setTimeout(() => {
        const currentNodes = latestNodesRef.current;
        const currentStoreNodes = useWorkflowStore.getState().nodes;

        if (currentNodes.length === 0 && currentStoreNodes.length > 0) {
          console.warn('[WorkflowCanvas] Skipped store sync: React Flow has 0 nodes but store has', currentStoreNodes.length);
          return;
        }

        isInternalNodeUpdate.current = true;
        setStoreNodes(fromReactFlowNodes(currentNodes));
      }, 0);
    },
    [onNodesChange, setStoreNodes]
  );

  // Handle edge changes (selection only).
  // We never call setStoreEdges(fromReactFlowEdges(...)) here because
  // fromReactFlowEdges only captures a subset of fields, which would wipe
  // properties like markerEnd, labelStyle, type etc. set via updateEdge().
  // Instead we only sync the `selected` flag back to the store by merging
  // it onto the existing store edge, preserving all other fields.
  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const nonRemoveChanges = changes.filter(c => c.type !== 'remove');
      onEdgesChange(nonRemoveChanges);

      const selectChanges = nonRemoveChanges.filter(c => c.type === 'select');
      if (selectChanges.length === 0) return;

      setTimeout(() => {
        const currentStoreEdges = useWorkflowStore.getState().edges;
        const updatedStoreEdges = currentStoreEdges.map((edge) => {
          const sc = selectChanges.find(c => c.type === 'select' && (c as any).id === edge.id);
          return sc ? { ...edge, selected: (sc as any).selected } : edge;
        });

        // Mark internal so the storeEdges effect skips the redundant setEdges call
        // (ReactFlow already applied the selection via onEdgesChange above).
        isInternalEdgeUpdate.current = true;
        setStoreEdges(updatedStoreEdges);

        const selectedEdgeIds = updatedStoreEdges.filter(e => e.selected).map(e => e.id);
        setSelectedEdges(selectedEdgeIds);
      }, 0);
    },
    [onEdgesChange, setStoreEdges, setSelectedEdges]
  );

  // Handle new connection
  const onConnect = useCallback(
    (connection: Connection) => {
      // Log raw connection object for debugging
      console.log('[WorkflowCanvas] onConnect called with connection:', {
        source: connection.source,
        target: connection.target,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
        rawConnection: connection,
      });

      // Validate connection using port rules
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (!sourceNode || !targetNode) {
        console.warn('[WorkflowCanvas] Could not validate connection: source or target node not found');
        return;
      }

      const sourceNodeCategory = getNodeDisplayProps(sourceNode.data).category;
      const targetNodeCategory = getNodeDisplayProps(targetNode.data).category;
      const sourceHandleId = connection.sourceHandle || 'main';

      // Enforce component port exclusivity before further validation
      if (connection.sourceHandle === 'my-components' && connection.targetHandle !== 'component') {
        showToast('warning', 'The "my-components" port can only connect to a "component" port');
        return;
      }
      if (connection.targetHandle === 'component' && connection.sourceHandle !== 'my-components') {
        showToast('warning', 'The "component" port can only receive connections from a "my-components" port');
        return;
      }

      // Map handle ID to port type (same as in NodeSearchModal)
      const getPortType = (handleId: string) => {
        switch (handleId?.toLowerCase()) {
          case 'trigger': return 'trigger';
          case 'llm': return 'llm';
          case 'memory': return 'memory';
          case 'tool': return 'tool';
          case 'extends': return 'agentInherit';
          case 'team-leader': return 'agentParticipation';
          case 'knowledgebase': return 'knowledgeBase';
          case 'my-components': return 'component';
          case 'input':
          case 'main':
          default: return 'output';
        }
      };

      let portType = getPortType(sourceHandleId);

      // Special case: Tool node outputs should only connect to tools
      if (portType === 'output' && sourceNodeCategory === 'tool') {
        portType = 'tool';
      }

      // Validate using the port rules
      const validation = validateNodeForPort(targetNodeCategory as NodeCategory, portType as PortType);

      if (!validation.isValid) {
        console.warn('[WorkflowCanvas] Invalid connection blocked:', {
          sourceNode: sourceNode.data?.displayName,
          targetNode: targetNode.data?.displayName,
          sourceCategory: sourceNodeCategory,
          targetCategory: targetNodeCategory,
          reason: validation.reason,
        });
        showToast('warning', `Cannot connect: ${validation.reason}`);
        return; // Prevent the connection
      }

      console.log('[WorkflowCanvas] Valid connection allowed:', {
        source: sourceNode.data?.displayName,
        target: targetNode.data?.displayName,
        portType,
        sourceHandle: connection.sourceHandle,
        targetHandle: connection.targetHandle,
      });

      const edgeType = useSmartEdges ? 'smart' : 'smoothstep';

      // Update React Flow edges immediately
      setEdges((eds) => addEdge({
        ...connection,
        id: generateId('edge'),
        type: edgeType,
        animated: false,
      }, eds));

      // Also update store
      // CRITICAL: Preserve exact handle IDs from React Flow Connection
      // AI Agent has custom input ports (main, base-node, team-member, llm, memory, tool)
      const newEdge = {
        id: generateId('edge'),
        source: connection.source!,
        target: connection.target!,
        sourceHandle: connection.sourceHandle || 'main',
        targetHandle: connection.targetHandle || 'main',
        type: edgeType as any,
        animated: false,
      };

      console.log('[WorkflowCanvas] Creating new edge for store:', {
        id: newEdge.id,
        source: newEdge.source,
        target: newEdge.target,
        sourceHandle: newEdge.sourceHandle,
        targetHandle: newEdge.targetHandle,
        targetNodeType: targetNode.type,
        targetNodeCategory: targetNodeCategory,
      });

      // Mark as internal update to prevent sync loop
      isInternalEdgeUpdate.current = true;
      addStoreEdge(newEdge as any);
    },
    [addStoreEdge, setEdges, useSmartEdges, nodes]
  );

  // Handle drop
  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (isReadOnly || isCanvasLocked) {
        showToast(
          'info',
          isCanvasLocked
            ? 'Canvas is locked — click the padlock icon (bottom-left) to unlock before adding nodes.'
            : 'Canvas is read-only in this mode — switch to Design mode to add nodes.'
        );
        return;
      }
      if (reactFlowInstance) {
        onDrop(event, reactFlowInstance);
      }
    },
    [reactFlowInstance, onDrop, isReadOnly, isCanvasLocked, showToast]
  );

  // Primary selection handler — React Flow calls this with the current full selection
  // after every selection change (node click, edge click, pane click, rubber-band, etc.).
  // This is the single source of truth for syncing React Flow selection → UIStore.
  const onSelectionChange = useCallback(
    ({ nodes: selNodes, edges: selEdges }: { nodes: any[]; edges: any[] }) => {
      setSelectedNodes(selNodes.map((n: any) => n.id));
      setSelectedEdges(selEdges.map((e: any) => e.id));
      if (selNodes.length > 0 || selEdges.length > 0) {
        setSelectedHandle(null);
      }
    },
    [setSelectedNodes, setSelectedEdges, setSelectedHandle]
  );

  // Clear selection when the user clicks empty canvas space.
  // Also closes any open context menu / toolbar dropdown: React Flow's pan handler
  // calls stopImmediatePropagation() on pane mousedown (to support panOnDrag), which
  // prevents other components' document-level mousedown listeners from ever seeing
  // clicks on the canvas background. The custom event lets those components (e.g.
  // TopToolbar's View/Edit menus) react to canvas clicks without depending on that.
  const onPaneClick = useCallback(() => {
    clearSelection();
    closeContextMenu();
    window.dispatchEvent(new CustomEvent('flow-studio:pane-click'));
  }, [clearSelection, closeContextMenu]);

  // Handle edge click - clear handle selection (node/edge selection handled by onSelectionChange)
  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, _edge: any) => {
      setSelectedHandle(null);
    },
    [setSelectedHandle]
  );

  const handleSelectAll = useCallback(() => {
    const allIds = latestNodesRef.current.map(n => n.id);
    setSelectedNodes(allIds);
  }, [setSelectedNodes]);

  const handleFitView = useCallback(() => {
    reactFlowInstance?.fitView({ padding: 0.2, duration: 400 });
  }, [reactFlowInstance]);

  React.useEffect(() => {
    const handler = () => reactFlowInstance?.fitView({ padding: 0.2, duration: 400 });
    window.addEventListener('flow-studio:fit-view', handler);
    return () => window.removeEventListener('flow-studio:fit-view', handler);
  }, [reactFlowInstance]);

  // Auto-save positions after drag ends (debounced 800 ms)
  const handleNodeDragStop = useCallback(() => {
    if (isReadOnly || isCanvasLocked) return;

    const currentNodes = latestNodesRef.current;
    if (currentNodes.length === 0) return;

    // Sync immediately (no setTimeout) so positions are in store before save
    isInternalNodeUpdate.current = true;
    setStoreNodes(fromReactFlowNodes(currentNodes));

    if (autoSaveEnabled) {
      if (positionSaveTimerRef.current) clearTimeout(positionSaveTimerRef.current);
      positionSaveTimerRef.current = setTimeout(() => {
        useWorkflowStore.getState().saveWorkflowToApi();
      }, 800);
    }
  }, [isReadOnly, isCanvasLocked, autoSaveEnabled, setStoreNodes]);

  // Clean up debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (positionSaveTimerRef.current) clearTimeout(positionSaveTimerRef.current);
    };
  }, []);

  // Recompute the open context menu's screen position from its flow-space anchor on every
  // viewport change, so it stays visually attached to its target while panning/zooming
  // (drag or scroll) instead of being left behind at a stale screen coordinate.
  // `viewport` (from useViewport()) is only a dependency to force recomputation on every
  // pan/zoom frame — flowToScreenPosition itself always reads the live transform.
  const menuScreenPosition = React.useMemo(() => {
    if (!contextMenu?.flowPosition) return contextMenu?.position;
    if (!reactFlowInstance) return contextMenu.position;
    return reactFlowInstance.flowToScreenPosition(contextMenu.flowPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, viewport, reactFlowInstance]);

  return (
    <div
      ref={reactFlowWrapper}
      className={`react-flow-wrapper ${handMode ? 'react-flow-wrapper--hand' : ''} ${theme === 'dark' ? 'react-flow-wrapper--dark' : 'react-flow-wrapper--light'} ${isReadOnly ? 'react-flow-wrapper--readonly' : ''}`}
      onContextMenu={handleCanvasContextMenu}
    >
      {/* Read-only mode indicator */}
      {isReadOnly && (
        <div
          className={`react-flow-readonly-indicator ${mode === 'execution' ? 'react-flow-readonly-indicator--execution' : 'react-flow-readonly-indicator--evaluation'}`}
        >
          {mode === 'execution' ? <LockIcon size={13} /> : <BarChartIcon size={15} />}
          <span>{mode === 'execution' ? 'Execution Mode - Canvas is read-only' : 'Evaluation Mode - Canvas is read-only'}</span>
        </div>
      )}
      <ReactFlow
        nodes={displayNodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        onSelectionChange={onSelectionChange}
        onPaneClick={onPaneClick}
        onEdgeClick={onEdgeClick}
        onNodeContextMenu={handleNodeContextMenu as any}
        onNodeDragStop={handleNodeDragStop}
        onInit={(instance) => {
          setReactFlowInstance(instance);
          setTimeout(() => instance.fitView({ padding: 0.15, maxZoom: 1, duration: 0 }), 50);
        }}
        onMoveEnd={(_e, vp) => saveViewport(vp)}
        onDrop={handleDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        snapToGrid={snapToGrid}
        snapGrid={[gridSize, gridSize]}
        nodesDraggable={!isReadOnly && !isCanvasLocked && !handMode}
        nodesConnectable={!isReadOnly && !isCanvasLocked && !handMode}
        elementsSelectable={!handMode}
        nodesFocusable={!handMode}
        edgesFocusable={!handMode}
        panOnDrag={[0, 1, 2]}
        panOnScroll
        panOnScrollMode={PanOnScrollMode.Free}
        zoomOnScroll={false}
        selectionOnDrag={false}
        deleteKeyCode={null}
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={savedViewport ?? { x: 0, y: 0, zoom: 1 }}
        attributionPosition="bottom-right"
        connectionLineType={ConnectionLineType.SmoothStep}
        defaultEdgeOptions={{
          type: useSmartEdges ? 'smart' : 'smoothstep',
          animated: false,
        }}
      >
        <Background
          gap={10}
          size={1.5}
          color={theme === 'dark' ? '#2d3748' : '#cbd5e0'}
          variant={BackgroundVariant.Dots}
          className={theme === 'dark' ? 'react-flow__background--gradient-dark' : 'react-flow__background--gradient-light'}
        />
        {/* All controls in one vertical group at the bottom-left */}
        <Controls position="bottom-left" showInteractive={false}>
          <CanvasControls handMode={handToggled} onToggleHand={toggleHand} />
        </Controls>
        {showMinimap && <MiniMap zoomable pannable />}
      </ReactFlow>

      {/* Node context menu */}
      {contextMenu && contextMenu.type === 'node' && menuScreenPosition && (
        <NodeContextMenu
          nodeId={contextMenu.targetId!}
          position={menuScreenPosition}
          onClose={closeContextMenu}
          onPinOutput={(id) => setPinDialogNodeId(id)}
          zoom={viewport.zoom}
        />
      )}

      {/* Canvas context menu */}
      {contextMenu && contextMenu.type === 'canvas' && menuScreenPosition && (
        <CanvasContextMenu
          position={menuScreenPosition}
          onClose={closeContextMenu}
          onFitView={handleFitView}
          onSelectAll={handleSelectAll}
          zoom={viewport.zoom}
        />
      )}

      {/* Pin node dialog — lives here so it survives NodeContextMenu unmount */}
      {pinDialogNodeId && (
        <PinNodeDialog
          nodeId={pinDialogNodeId}
          onClose={() => setPinDialogNodeId(null)}
        />
      )}

      {/* Keyboard-delete confirmation dialog */}
      <ConfirmDialog
        isOpen={pendingDeleteNodeIds.length > 0 || pendingDeleteEdgeIds.length > 0}
        onClose={clearPendingDelete}
        onConfirm={() => {
          (pendingDeleteNodeIds as string[]).forEach((id) => deleteNode(id));
          (pendingDeleteEdgeIds as string[]).forEach((id) => deleteEdge(id));
          clearSelection();
          clearPendingDelete();
          const nodeCount = pendingDeleteNodeIds.length;
          const edgeCount = pendingDeleteEdgeIds.length;
          const parts = [];
          if (nodeCount > 0) parts.push(`${nodeCount} node${nodeCount > 1 ? 's' : ''}`);
          if (edgeCount > 0) parts.push(`${edgeCount} connection${edgeCount > 1 ? 's' : ''}`);
          showToast('info', `Deleted ${parts.join(' and ')}`);
        }}
        title="Delete"
        message={
          pendingDeleteNodeIds.length > 0 && pendingDeleteEdgeIds.length > 0
            ? `Delete ${pendingDeleteNodeIds.length} node(s) and ${pendingDeleteEdgeIds.length} connection(s)? This cannot be undone.`
            : pendingDeleteNodeIds.length > 0
            ? `Delete ${pendingDeleteNodeIds.length} node(s)? This cannot be undone.`
            : `Delete ${pendingDeleteEdgeIds.length} connection(s)? This cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

    </div>
  );
};
